/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {KeybindCommand, KeyCombo} from '@app/stores/KeybindStore';
import {isDesktop, isFirefoxBrowser} from '@app/utils/NativeUtils';

type ZoomKeybindAction = Extract<KeybindCommand, 'zoom_in' | 'zoom_out' | 'zoom_reset'>;

const ZOOM_ACTIONS = new Set<ZoomKeybindAction>(['zoom_in', 'zoom_out', 'zoom_reset']);

const ZOOM_IN_KEYS = new Set(['=', '+', 'equal', 'numpadadd']);
const ZOOM_OUT_KEYS = new Set(['-', '_', 'minus', 'numpadsubtract']);
const ZOOM_RESET_KEYS = new Set(['0', ')', 'digit0', 'numpad0']);

interface ZoomKeybindEnvironment {
	isDesktop?: boolean;
	isFirefoxBrowser?: boolean;
}

const normalizeKey = (rawKey: string | undefined): string => {
	return (rawKey ?? '').trim().toLowerCase();
};

const usesBrowserZoomModifier = (combo: KeyCombo): boolean => {
	return !!(combo.ctrlOrMeta || combo.ctrl || combo.meta);
};

export function shouldWarnAboutFirefoxWebZoomShortcuts(
	environment: ZoomKeybindEnvironment = {},
): boolean {
	const desktop = environment.isDesktop ?? isDesktop();
	const firefox = environment.isFirefoxBrowser ?? isFirefoxBrowser();

	return !desktop && firefox;
}

export function isFirefoxWebReservedZoomShortcut(
	action: KeybindCommand,
	combo: KeyCombo,
	environment: ZoomKeybindEnvironment = {},
): boolean {
	if (!ZOOM_ACTIONS.has(action as ZoomKeybindAction)) {
		return false;
	}

	if (!shouldWarnAboutFirefoxWebZoomShortcuts(environment)) {
		return false;
	}

	if (!usesBrowserZoomModifier(combo) || combo.alt) {
		return false;
	}

	const normalizedKey = normalizeKey(combo.code ?? combo.key);

	switch (action) {
		case 'zoom_in':
			return ZOOM_IN_KEYS.has(normalizedKey);
		case 'zoom_out':
			return ZOOM_OUT_KEYS.has(normalizedKey);
		case 'zoom_reset':
			return ZOOM_RESET_KEYS.has(normalizedKey);
		default:
			return false;
	}
}
